// Meta (Facebook/Instagram) Pixel — loaded ONLY after the visitor actively
// consented, and fully switchable off/on again at any time. Without consent
// no Meta script is fetched and no event is sent. Purchase is additionally
// reported server-side (Conversions API) using the same event id.

const KEY = 'nn_marketing_consent'; // 'granted' | 'denied'
const EVT = 'nn-consent-change';

type Consent = 'granted' | 'denied' | null;

let pixelId = '';
// Pixel des Veranstalters, dem die gerade angezeigte Seite gehört. Wird nur
// gesetzt, solange der Besucher auf dessen Event/Checkout ist.
let partnerPixelId = '';
// Event, über den der Partner-Pixel ermittelt wurde (für serverseitige Weiterleitung per CAPI).
let partnerEventRef = '';
const initedPartners = new Set<string>();
let loaded = false;
let loading: Promise<void> | null = null;

export function getConsent(): Consent {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch (e) { return null; }
}

export const hasConsent = () => getConsent() === 'granted';

export function onConsentChange(cb: () => void) {
  window.addEventListener(EVT, cb);
  return () => window.removeEventListener(EVT, cb);
}

function readCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch (e) { return ''; }
}

function deleteMetaCookies() {
  const host = location.hostname;
  const parts = host.split('.');
  const domains = [host, '.' + host];
  if (parts.length > 2) domains.push('.' + parts.slice(-2).join('.'));
  else domains.push('.' + host);
  ['_fbp', '_fbc'].forEach(n => {
    document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    domains.forEach(d => { document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`; });
  });
}

function loadPixel(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (loading) return loading;
  loading = (async () => {
    try {
      const r = await fetch('/api/meta/config', { cache: 'no-store' });
      const j = await r.json().catch(() => ({}));
      pixelId = String((j && j.pixelId) || '');
    } catch (e) { pixelId = ''; }
    if (!pixelId || !hasConsent()) { loading = null; return; }
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!w._fbq) w._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(s);
    }
    w.fbq('init', pixelId);
    loaded = true;
    w.fbq('consent', 'grant');
    w.fbq('trackSingle', pixelId, 'PageView');
    activatePartner();
  })();
  return loading;
}

// Initialisiert den Partner-Pixel (einmal) und meldet dessen PageView.
// `force`: PageView auch dann melden, wenn der Pixel schon initialisiert ist (neuer Event-/Checkout-Aufruf).
function activatePartner(force = false) {
  const w = window as any;
  if (!loaded || !w.fbq || !hasConsent() || !partnerPixelId) return;
  if (initedPartners.has(partnerPixelId)) {
    if (force) { try { w.fbq('trackSingle', partnerPixelId, 'PageView'); } catch (e) {} }
    return;
  }
  initedPartners.add(partnerPixelId);
  w.fbq('init', partnerPixelId);
  w.fbq('trackSingle', partnerPixelId, 'PageView');
}

export function getPartnerPixelId() { return partnerPixelId; }

export function clearPartnerPixel() { partnerPixelId = ''; partnerEventRef = ''; }

// Holt den Pixel des Veranstalters zu einem Event und aktiviert ihn (nur mit Einwilligung).
export async function setPartnerPixelForEvent(eventId: string): Promise<string> {
  partnerPixelId = '';
  partnerEventRef = '';
  try {
    if (!eventId) return '';
    const r = await fetch(`/api/meta/config?eventId=${encodeURIComponent(String(eventId))}`, { cache: 'no-store' });
    const j = await r.json().catch(() => ({}));
    partnerPixelId = /^\d{8,20}$/.test(String(j && j.partnerPixelId || '')) ? String(j.partnerPixelId) : '';
  } catch (e) { partnerPixelId = ''; }
  if (partnerPixelId) partnerEventRef = String(eventId);
  if (partnerPixelId && hasConsent()) {
    if (loaded) activatePartner(true); else void loadPixel();
  }
  return partnerPixelId;
}

export function setConsent(granted: boolean) {
  try { localStorage.setItem(KEY, granted ? 'granted' : 'denied'); } catch (e) {}
  const w = window as any;
  if (granted) {
    if (loaded && w.fbq) { w.fbq('consent', 'grant'); w.fbq('trackSingle', pixelId, 'PageView'); activatePartner(); }
    else void loadPixel();
  } else {
    // Revoke: stop all further tracking, delete Meta cookies.
    try { if (w.fbq) w.fbq('consent', 'revoke'); } catch (e) {}
    deleteMetaCookies();
  }
  try { window.dispatchEvent(new Event(EVT)); } catch (e) {}
}

export function initMetaTracking() {
  if (hasConsent()) void loadPixel();
}

export function newEventId(prefix = 'ev') {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

// Identifiers Meta needs for matching; only exposed when the visitor consented.
export function getMarketingPayload(eventId: string): Record<string, any> | null {
  if (!hasConsent()) return null;
  return {
    consent: true,
    source: 'website',
    eventId,
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
    url: location.href
  };
}

// Feuert an den NightNavi-Pixel und, falls gesetzt, an den Pixel des Veranstalters.
// `extraPixelId` erlaubt einen Partner-Pixel ohne Seitenkontext (z. B. Purchase nach Stripe-Redirect).
export function trackMeta(eventName: string, customData?: Record<string, any>, eventId?: string, extraPixelId?: string) {
  if (!hasConsent()) return;
  const w = window as any;
  // Browser- und Server-Event (CAPI des Veranstalters) teilen sich eine ID, damit Meta dedupliziert.
  const relay = !extraPixelId && !!partnerPixelId && !!partnerEventRef && (eventName === 'ViewContent' || eventName === 'InitiateCheckout');
  if (relay && !eventId) eventId = newEventId(eventName === 'ViewContent' ? 'vc' : 'ic');
  if (relay) {
    const ref = partnerEventRef;
    try {
      fetch('/api/meta/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent: true, eventName, eventId, partnerEventRef: ref, url: location.href, source: 'website', fbp: readCookie('_fbp'), fbc: readCookie('_fbc'), customData: customData || {} }),
        keepalive: true
      }).catch(() => {});
    } catch (e) {}
  }
  const fire = () => {
    const targets = [pixelId];
    const partner = extraPixelId || partnerPixelId;
    if (partner && /^\d{8,20}$/.test(partner)) {
      if (!initedPartners.has(partner)) { initedPartners.add(partner); try { w.fbq('init', partner); } catch (e) {} }
      if (!targets.includes(partner)) targets.push(partner);
    }
    targets.forEach(id => {
      try {
        if (eventId) w.fbq('trackSingle', id, eventName, customData || {}, { eventID: eventId });
        else w.fbq('trackSingle', id, eventName, customData || {});
      } catch (e) {}
    });
  };
  if (loaded && w.fbq) fire();
  else loadPixel().then(() => { if (loaded && hasConsent()) fire(); });
}
